import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  withStyles,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { searchPharmacies } from "../utils/asyncServerRequests";
import { arrayHasValue, formatPhoneNumber } from "../utils/resources";

const styles = theme => ({
  wrapper: {
    margin: theme.spacing.unit,
    marginBottom: "1em",
    marginTop: "2em",
    marginLeft: "16px",
    justifyContent: "left",
  },
  content: {
    minWidth: "500px",
    maxWidth: "700px",
  },
  dataRow: {
    marginBottom: theme.spacing.unit,
    //padding: theme.spacing.unit,
  },
  label: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  value: {
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  emptyValue: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing.unit,
    top: theme.spacing.unit,
    color: theme.palette.grey[500],
  },
});

const PharmacySearchModal = props => {
  const { classes, open, onClose, pharmacyData, onSelectPharmacy } = props;
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && pharmacyData) {
      performPharmacySearch();
    }
  }, [open, pharmacyData]);

  const handleSelectPharmacy = pharmacy => {
    if (onSelectPharmacy) {
      onSelectPharmacy(pharmacy);
    }
    onClose();
  };

  const performPharmacySearch = async () => {
    setLoading(true);
    setError(null);
    setSearchResults([]);

    try {
      const response = await searchPharmacies(pharmacyData);

      if (response.code < 400) {
        if (!arrayHasValue(response.data)) {
          setError(
            "No pharmacies found matching your search criteria. Please change your search criteria and try again."
          );
        } else {
          setSearchResults(response.data);
        }
      } else {
        throw response;
      }
    } catch (err) {
      console.error("Pharmacy search failed:", err);
      const errorMessage = String(
        err.message || "Failed to search pharmacies. Please try again."
      );
      if (errorMessage.toLowerCase().includes("timeout")) {
        setError(
          "Search timed out. Please add more specific details to your search criteria and try again."
        );
      } else {
        setError("Failed to search pharmacies. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Pharmacy Search Results</span>
          <Typography variant="body2" color="textSecondary" component="span">
            {loading
              ? "Searching pharmacies..."
              : `Found ${searchResults.length} pharmacies`}
          </Typography>
        </div>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loading && (
          <Grid container spacing={16} style={{ padding: 20 }}>
            <Grid item xs={12}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginTop: 10 }}>
                Searching pharmacies...
              </Typography>
            </Grid>
          </Grid>
        )}

        {error && (
          <Grid container spacing={16}>
            <Grid item xs={12}>
              <Typography variant="body1" color="error" style={{ padding: 20 }}>
                {error}
              </Typography>
            </Grid>
          </Grid>
        )}

        {!loading && !error && searchResults.length === 0 && (
          <Grid container spacing={16}>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ padding: 20 }}>
                No pharmacies found matching your search criteria.
              </Typography>
            </Grid>
          </Grid>
        )}

        {!loading && !error && searchResults.length > 0 && (
          <Grid container spacing={16}>
            {searchResults.map((pharmacy, index) => (
              <Grid item xs={12} key={pharmacy.id || index}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div className={classes.dataRow} style={{ flex: 1 }}>
                    <Typography className={classes.label} variant="h6">
                      {pharmacy.name}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      {pharmacy.address_line_1 || "No address"}
                      {pharmacy.address_line_2 &&
                        `, ${pharmacy.address_line_2}`}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      {pharmacy.address_city || ""}
                      {pharmacy.address_state
                        ? `, ${pharmacy.address_state}`
                        : ""}
                      {pharmacy.address_zipcode
                        ? ` ${pharmacy.address_zipcode}`
                        : ""}
                    </Typography>

                    {pharmacy.primary_phone_number && (
                      <Typography variant="body2">
                        <strong>Phone:</strong>{" "}
                        {formatPhoneNumber(pharmacy.primary_phone_number)}
                      </Typography>
                    )}

                    {pharmacy.primary_fax_number && (
                      <Typography variant="body2">
                        <strong>Fax:</strong>{" "}
                        {formatPhoneNumber(pharmacy.primary_fax_number)}
                      </Typography>
                    )}

                    {pharmacy.pharmacy_specialties &&
                      Array.isArray(pharmacy.pharmacy_specialties) &&
                      pharmacy.pharmacy_specialties.length > 0 && (
                        <Typography variant="body2">
                          <strong>Specialties:</strong>{" "}
                          {pharmacy.pharmacy_specialties.join(", ")}
                        </Typography>
                      )}
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleSelectPharmacy(pharmacy)}
                    style={{ marginLeft: 16, marginTop: 8 }}
                  >
                    Select
                  </Button>
                </div>
                {index < searchResults.length - 1 && <Divider />}
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && (
          <Grid item xs={12} style={{ marginTop: 16 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Search Criteria:</strong>{" "}
              {Object.entries(pharmacyData || {})
                .filter(([key, value]) => value)
                .map(([key, value]) => `${key}: ${value}`)
                .join(", ") || "No criteria provided"}
            </Typography>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(styles)(PharmacySearchModal);
